/**
 * Pure helpers for rendering an omnisearch string as filter pills.
 *
 * The omnisearch string stays the single source of truth — it is what the URL
 * carries and what every backend resolver parses. These helpers only split it
 * into the parts a pill UI needs and put it back together again, so the pill
 * row is a presentation layer and nothing about the query language changes.
 */
import { extractKeyValuePairs } from "encamp-omnisearch/src/utils/util";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { FilterKey, FilterKeyType } from "hooks/useOmnisearchDatagrid";

/** One `key:value|value` term from an omnisearch string. */
export type OmnisearchPair = {
  key: string;
  values: string[];
};

export type SplitOmnisearch = {
  /** Key/value terms, one per key — duplicates of a key are merged. */
  pairs: OmnisearchPair[];
  /** Everything that was not part of a key/value term. */
  freeText: string;
};

/**
 * How a boolean filter's two values read on a pill.
 *
 * Matches the Yes/No the filter popover offers for a `boolean` field, so a pill
 * says the same thing the editor that set it says. (Its third option, "Any",
 * clears the filter, which removes the pill rather than labelling it.)
 */
const BOOLEAN_VALUE_LABELS: ReadonlyMap<string, string> = new Map([
  ["true", "Yes"],
  ["false", "No"],
]);

/** Filter types whose raw values are enum constants needing pretty-printing. */
const ENUM_LIKE_TYPES: ReadonlySet<FilterKeyType> = new Set<FilterKeyType>([
  "enum",
  "enumAutocomplete",
  "stateOfMatter",
]);

function collapseWhitespace(value: string): string {
  return value.replace(/\s+/g, " ").trim();
}

/**
 * Splits an omnisearch string into its key/value terms and the free text
 * around them, using the offsets the parser already reports so the free text
 * is whatever the terms did not occupy.
 */
export function splitOmnisearch(omnisearch: string): SplitOmnisearch {
  const source = omnisearch ?? "";
  const extracted = extractKeyValuePairs(source);

  let freeText = "";
  let cursor = 0;
  extracted.forEach(({ start, end }) => {
    freeText += source.slice(cursor, start);
    cursor = end;
  });
  freeText += source.slice(cursor);

  // Merge repeated keys so a single field never renders as two pills.
  const valuesByKey = new Map<string, string[]>();
  extracted.forEach(({ key, value }) => {
    const existing = valuesByKey.get(key);
    if (!existing) {
      valuesByKey.set(key, Array.from(new Set(value)));
      return;
    }
    value.forEach((entry) => {
      if (!existing.includes(entry)) {
        existing.push(entry);
      }
    });
  });

  const pairs = Array.from(valuesByKey, ([key, values]) => ({ key, values }));

  return { pairs, freeText: collapseWhitespace(freeText) };
}

/**
 * Quotes a value that would otherwise be mis-parsed on the way back in.
 * Mirrors the quoting the filter popover already applies when it serializes.
 *
 * The query language has no escape for a literal `"`, so the character is
 * dropped rather than doubled: `"a""b"` re-parses as `a`, silently losing
 * everything past the quote, while `"ab"` survives whole. A parsed value can
 * never contain a `"` — the tokenizer either strips a quoted group's own quotes
 * or excludes the character — so this only guards a value reaching here from
 * somewhere other than the parser.
 */
export function quoteValue(value: string): string {
  const unquotable = value.replace(/"/g, "");
  if (!/[\s:|,]/.test(unquotable)) {
    return unquotable;
  }
  return `"${unquotable}"`;
}

/**
 * Rebuilds an omnisearch string from pills plus free text.
 *
 * Free text comes first so the result reads the way a user would have typed
 * it, and so a query that was only free text is returned unchanged.
 */
export function serializeOmnisearch(pairs: OmnisearchPair[], freeText: string): string {
  const terms = pairs.map(({ key, values }) => {
    const rendered = values.filter((value) => value.length > 0).map(quoteValue);
    if (rendered.length === 0) {
      return "";
    }
    return `${key}:${rendered.join("|")}`;
  });

  return [collapseWhitespace(freeText ?? ""), ...terms].filter((part) => part.length > 0).join(" ");
}

/** Drops every term for `key`, leaving the other terms and the free text. */
export function removePairFromOmnisearch(omnisearch: string, key: string): string {
  const { pairs, freeText } = splitOmnisearch(omnisearch);
  return serializeOmnisearch(
    pairs.filter((pair) => pair.key !== key),
    freeText
  );
}

function formatPillValue(
  value: string,
  filterKey: FilterKey | undefined,
  valueLabels: ReadonlyMap<string, string> | undefined
): string {
  // A resolved label is the most specific thing we know about a value — it is
  // how an opaque id (facilityId:<uuid>) becomes the name a user recognizes.
  const resolved = valueLabels?.get(value);
  if (resolved) {
    return resolved;
  }
  if (filterKey?.enumPresentationFunction) {
    return filterKey.enumPresentationFunction(value);
  }
  const filterKeyType = filterKey?.filterKeyType;
  if (filterKeyType === "boolean") {
    return BOOLEAN_VALUE_LABELS.get(value.toLowerCase()) ?? value;
  }
  if (filterKeyType && ENUM_LIKE_TYPES.has(filterKeyType)) {
    return prettyPrintEnumValue(value, undefined, filterKey?.ignoreStateAbbreviations);
  }
  return value;
}

/**
 * Human-readable pill text — the column's header plus its values, with enum
 * constants printed the same way the filter popover prints them.
 *
 * `valueLabels` maps a raw omnisearch value to what should be shown for it,
 * for filters whose values are ids. Values it doesn't cover fall back to the
 * raw value, which is also what shows while a lookup is still loading.
 */
export function pillLabel(
  pair: OmnisearchPair,
  filterKey: FilterKey | undefined,
  valueLabels?: ReadonlyMap<string, string>
): string {
  // Keys the grid doesn't declare as filters (page-level controls like a
  // reporting-year selector) have no header, so present the key itself rather
  // than leaking camelCase into the UI.
  const header = filterKey?.header ?? prettyPrintEnumValue(pair.key);
  const values = pair.values.map((value) => formatPillValue(value, filterKey, valueLabels));
  return `${header}: ${values.join(", ")}`;
}

/**
 * True when the text ends in a half-typed `key:value` term.
 *
 * Used to hold off on rewriting the query while someone is mid-term, so a
 * pill does not flash into existence for `state:C` on the way to `state:CA`.
 */
export function hasIncompleteTerm(text: string): boolean {
  return /(?:^|\s)[^:\s]+:\S*$/.test(text);
}

function mergePairLists(
  base: OmnisearchPair[],
  incoming: OmnisearchPair[],
  isMultiValue: (key: string) => boolean
): OmnisearchPair[] {
  const merged = base.map((pair) => ({ key: pair.key, values: [...pair.values] }));
  incoming.forEach((pair) => {
    const existing = merged.find((candidate) => candidate.key === pair.key);
    if (!existing) {
      merged.push({ key: pair.key, values: [...pair.values] });
      return;
    }
    // A single-select filter holds one value, so a typed one replaces what is
    // there rather than piling up a list its editor could never show.
    if (!isMultiValue(pair.key)) {
      existing.values = [...pair.values];
      return;
    }
    pair.values.forEach((value) => {
      if (!existing.values.includes(value)) {
        existing.values.push(value);
      }
    });
  });
  return merged;
}

/**
 * Folds any complete `key:value` terms typed into the search box into the
 * existing pills, and reports the text that should remain in the box.
 *
 * This is what keeps typing the query language a first-class way to filter:
 * `state:CA` plus a space becomes a pill instead of staying as raw syntax.
 *
 * `isMultiValue` decides, per key, whether a typed value joins the existing
 * ones or replaces them. Defaults to joining.
 *
 * `folded` says whether a term actually became a pill. Callers should only
 * replace what the user typed when it did — `freeText` is whitespace-collapsed,
 * so pushing it back on every keystroke would eat a trailing space as it is
 * typed and make the space key look broken.
 */
export function commitTypedText(
  omnisearch: string,
  typedText: string,
  isMultiValue: (key: string) => boolean = () => false
): { omnisearch: string; freeText: string; folded: boolean } {
  const { pairs } = splitOmnisearch(omnisearch);
  const typed = splitOmnisearch(typedText);
  const merged = mergePairLists(pairs, typed.pairs, isMultiValue);
  return {
    omnisearch: serializeOmnisearch(merged, typed.freeText),
    freeText: typed.freeText,
    folded: typed.pairs.length > 0,
  };
}
